<template>

	<div class="container-fluid bg-white">

		<!--------------- ToolBar --------------->
		<toolbar>
			<h2>
				<i class="fa fa-chart-pie"></i>
				Services Reporting
			</h2>

			<projects-toolbar-controls
				:show-search="false"
				:show-new='false'
				:show-quote="false"
				:is-services="true"
				:active-nav-index="2">
			</projects-toolbar-controls>
		</toolbar>


		<!--------------- Tabs --------------->
		<div class="report-tab mx-5 my-4 row">

			<!--------------- Report Type --------------->
			<field
				:params="{
					type:'btn-group',
					tabStyling: true,
					disableNull: true,
					buttons:[
						{text:'Employees',value:'employees'},
						{text:'Customers',value:'customers'},
						{text:'Advanced', value:'advanced'},
					]}"
				v-model="reportType">
			</field>

			<!--------------- Advanced Report view --------------->
			<field
				v-if="reportType == 'advanced'"
				class="advanced-tabs"
				:params="{
					type:'btn-group',
					tabStyling: true,
					disableNull: true,
					buttons:[
						{ text:'Summary',value:'summary' },
						{ text:'Details',value:'details' },
					]}"
				v-model="advancedView">
			</field>
		</div>



		<!--------------- Tablelist view --------------->
		<service-report-table-view
			v-show="['employees', 'customers'].includes(reportType)"
			:all-data-fetched="allDataFetched"
			:all-services="allServices"
			:all-invoices="allInvoices"
			:all-employees="allEmployees"
			:all-customers="allCustomers"
			:compare="reportType"
			:refilter-data="refilterData"
			v-on:fetchAllData="fetchData(true)"
		></service-report-table-view>


		<!--------------- Advanced view --------------->
		<reports-services-all-view
			v-show="['advanced'].includes(reportType)"
			:all-data-fetched="allDataFetched"
			:all-services="allServices"
			:all-invoices="allInvoices"
			:all-employees="allEmployees"
			:all-customers="allCustomers"
			:compare="reportType"
			:view="advancedView"
			v-on:fetchAllData="fetchData(true)"
		></reports-services-all-view>

	</div>
</template>

<script>

import ServiceReportTableView from "@/pages/reports/ServiceReportTableView"
import Toolbar from "@/layout_fragments/Toolbar"
import ProjectsToolbarControls from "@/pages/project/ProjectsToolbarControls"
import Field from "@/components/Field"
import ReportsServicesAllView from "@/pages/reports/ReportsServicesAllView"

import Utilities from "@/mixins/Utilities"

export default {
	name: "ReportsServicesDashboard",
	components: {
		ServiceReportTableView,
		Toolbar,
		ProjectsToolbarControls,
		Field,
		ReportsServicesAllView
	},
	mixins: [Utilities],
	data() {
		return {
			// Our master lists of services, invoices, employees, and customers
			// This is passed to our children components for generating tables,
			// charts, filtering data, etc.  No other fetches take place in the
			// components used in this
			allServices: [],
			allInvoices: [],
			allBatchInvoices: [],
			allEmployees: [],
			allCustomers: [],
			allCategories: [],

			allDataFetched: false,
			reportType: 'employees',
			advancedView: 'summary',

			refilterData: 0		// Incremented each time data is fetched, forcing refilter in components
		}
	},
	methods: {

		setServiceCategoryCustomer() {
			this.allServices.forEach(service => {
				let customer = this.allCustomers.find(customer => service.customer_id == customer.id)
				service.Customer = customer ? customer : null

				let category = this.allCategories.find(category => service.category_id == category.id)
				service.categoryName = category ? category.name : "[uncategorized]"
			})
		},

		setServiceInvoices() {
			// Place each invoice/quote into the service they're tied to in allServices & calc tax/revenue
			// Called once after fetching services and invoices
			// Modifies this.allServices

			// Also sets customer names
			let vm = this

			// Create empty lists
			this.allServices.map(service => {
				service.invoice = null
				service.quote = null
				service.revenue = 0
				service.total_taxes = 0
				return service
			})

			// Note: this list contains quotes AND invoices
			this.allInvoices.forEach(invoice => {

				// Is batch invoice? Add to batchInvoices and continue
				if ("standalone_num" in invoice && invoice.standalone_num) {
					vm.allBatchInvoices.push(invoice)
					return
				}

				// Not a batch invoice.  What service is it tied to?
				let service = this.allServices.find(service => service.id == invoice.service_id)

				// Service exists? Attach to .quotes if quote; .invoices otherwise
				if (service) {

					if (invoice.is_quote == "1") {
						service.quote = invoice

					} else {

						service.invoice = invoice

						if ("pretax_total" in invoice && invoice.pretax_total)
							service.revenue = parseFloat(invoice.pretax_total)
					}
				}
			})
		},

		fetchData(fetchAll=false) {
			// Fetches all services, invoices/quotes, employees, and customers SEPARATELY
			// Called once when the page is loaded, where only some data is fetched
			// When more data is needed or required (eg. date range out of bounds) get the rest

			let vm = this
			vm.spinner(true)

			let serviceParams = {
				fields: ["id", "slug", "assigned_to", "job_num", "completed", "is_canceled", "customer_id", "category_id",
					"total_material_costs", "total_labour_costs", "total_equipment_costs", "permit_cost"],
				filters: []
			}

			let categoryParams = {
				fields: ["id", "name"]
			}

			let userParams = {
				fields: ["id", "first_name", "last_name"]
			}

			let customerParams = {
				fields: ["id", "name"],
				filters: [
					["is_customer", true]
				]
			}

			let invoiceParams = {
				fields: ["service_id", "standalone_num", 						// Used to sync to service(s)
					"is_quote", "quote_status", "quote_rejected",				// Used for quotes
					"taxable_total", "pretax_total", "applied_sales_tax_json",	// Used for revenue/tax
					"issue_date"	// for filtering (delete before live)
				],
				filters: [
					["is_freeform", "0"]
				]
			}

			// Do not fetch all? Only fetch from past month
			if (!fetchAll) {
				let oneMonthAgo = this.moment().subtract(1, 'month').format("YYYY-MM-DD")
				serviceParams.filters.push(["completed >", oneMonthAgo])
				invoiceParams.filters.push(["issue_date >", oneMonthAgo])
			} else {
				serviceParams.filters.push(["completed !=", null])
			}

			let promises = [
				this.getJSON("Service/get", serviceParams),
				this.getJSON("ServiceInvoice/get", invoiceParams),
			]

			// Employees not fetched yet and needed? Include them
			if (!this.allEmployees.length)
				promises.push(this.getJSON("User/get", userParams))

			// Customers not fetched yet? Include them
			if (!this.allCustomers.length)
				promises.push(this.getJSON("Contact/get", customerParams))

			// Service categories not fetched yet? Include them
			if (!this.allCategories.length)
				promises.push(this.getJSON("Category/get", categoryParams))


			// Put outside of response so that the request cannot be repeated before
			// the data is recieved
			vm.allDataFetched = fetchAll

			Promise.all(promises).then(responses => {

				if (responses[0].success)
					vm.allServices = responses[0].data

				if (responses[1].success) {
					vm.allInvoices = responses[1].data
				}

				// Note: Using 2,3,etc is good for now since all are fetched at once,
				// but may act weird if one of Employees,Customers, or Categories cannot be fetched

				// Employees included in fetch? Parse them
				if (2 in responses && responses[2].success) {
					vm.allEmployees = responses[2].data.map(emp => {
						if ('first_name' in emp && emp.first_name && 'last_name' in emp && emp.last_name)
							emp.name = emp.first_name + ' ' + emp.last_name
						return emp
					})
				}

				// Customers included in fetch?
				if (3 in responses && responses[3].success) {
					vm.allCustomers = responses[3].data
				}

				// Categories included in fetch?
				if (4 in responses && responses[4].success) {
					vm.allCategories = responses[4].data
				}

				// Response was unsuccessful? Reset allDataFetched to false
				if (!responses[0].success || !responses[1].success)
					vm.allDataFetched = false

				vm.spinner(false)
				vm.setServiceInvoices()		// Places categories and invoices into their service
				vm.setServiceCategoryCustomer()		// Places category and customer into their services
				this.refilterData += 1		// Finished aligning data, force refilter in components
			})
		},
	},
	created() {

		// On entering the dashboard, fetch the data for the first month
		this.fetchData(false)
	}
}
</script>

<style scoped>

.advanced-tabs {
	margin-left: 10%;
}
</style>